import React, {useEffect, useState} from "react";
import {makeStyles, withStyles} from "@material-ui/core/styles";
import {Box, IconButton, LinearProgress, Modal, Tooltip, Typography} from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import {PhotoCamera} from "@material-ui/icons";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import CloudUploadOutlinedIcon from "@material-ui/icons/CloudUploadOutlined";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import GetAppIcon from "@material-ui/icons/GetApp";
import ZoomInIcon from "@material-ui/icons/ZoomIn";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import {Lightbox} from "react-modal-image";
import {NotificationManager} from 'react-notifications';
import {usePrivateImage, privateMediaUrl} from '../../helpers/privateMedia';
import {mediaPreviewUrl, fetchPreviewBlob, useNearViewport} from '../../helpers/mediaPreview';
import {PHOTO_ACCEPT, photoUploadErrorMessage, validatePhotoFile} from '../../helpers/photo-upload';

const loadPriorityImage = (source, signal) => fetchPreviewBlob(source, signal, true);

export const BorderLinearProgress = withStyles(() => ({
    root: {
        height: 8,
        borderRadius: 4,
    },
    colorPrimary: {
        backgroundColor: "rgba(44, 71, 103, 0.10)",
    },
    bar: {
        borderRadius: 4,
        backgroundColor: "#b08d57",
    },
}))(LinearProgress);

const useStyles = makeStyles((theme) => ({
    card: {
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background: "#ffffff",
        border: "1px solid rgba(176, 141, 87, 0.22)",
        borderRadius: 14,
        boxShadow: "0 4px 16px rgba(44, 71, 103, 0.05)",
        transition: "box-shadow 220ms ease, transform 200ms ease, border-color 220ms ease",
        overflow: "hidden",
        "&:hover": {
            boxShadow: "0 12px 30px rgba(44, 71, 103, 0.10)",
            borderColor: "rgba(176, 141, 87, 0.45)",
        },
    },
    header: {
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: theme.spacing(1.25),
        padding: theme.spacing(2, 2.25, 1.25, 2.25),
    },
    titleWrap: {
        flex: "1 1 auto",
        minWidth: 0,
    },
    title: {
        fontWeight: 700,
        fontSize: "1rem",
        color: "#1a2733",
        lineHeight: 1.3,
        marginBottom: 4,
        display: "block",
    },
    hint: {
        fontSize: "0.78rem",
        color: "#5a6675",
        lineHeight: 1.45,
        display: "block",
    },
    uploadedBadge: {
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        flexShrink: 0,
        padding: "4px 9px",
        background: "rgba(61, 122, 90, 0.10)",
        color: "#2f6147",
        borderRadius: 999,
        fontSize: "0.65rem",
        fontWeight: 700,
        letterSpacing: 0.8,
        textTransform: "uppercase",
        border: "1px solid rgba(61, 122, 90, 0.22)",
        "& svg": {
            fontSize: 14,
        },
    },
    body: {
        padding: theme.spacing(0, 2.25),
        marginBottom: theme.spacing(1.5),
    },
    preview: {
        position: "relative",
        width: "100%",
        height: 200,
        borderRadius: 10,
        overflow: "hidden",
        background: "#1a2733",
        cursor: "pointer",
        "&:hover $previewOverlay": {
            opacity: 1,
        },
        "&:hover $previewImg": {
            transform: "scale(1.04)",
        },
    },
    previewImg: {
        width: "100%",
        height: "100%",
        objectFit: "cover",
        transition: "transform 300ms ease",
        display: "block",
    },
    previewOverlay: {
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "linear-gradient(180deg, rgba(30, 53, 83, 0) 40%, rgba(30, 53, 83, 0.7) 100%)",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        color: "#ffffff",
        fontWeight: 600,
        fontSize: "0.85rem",
        letterSpacing: 0.4,
        padding: theme.spacing(1.5),
        opacity: 0,
        transition: "opacity 220ms ease",
        gap: 6,
        "& svg": {
            fontSize: 18,
        },
    },
    empty: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: 200,
        borderRadius: 10,
        border: "2px dashed rgba(176, 141, 87, 0.42)",
        background: "rgba(247, 244, 238, 0.6)",
        cursor: "pointer",
        transition: "background 180ms ease, border-color 180ms ease, color 180ms ease",
        color: "#5a6675",
        gap: theme.spacing(0.5),
        textAlign: "center",
        padding: theme.spacing(2),
        margin: 0,
        boxSizing: "border-box",
        "&:hover": {
            background: "rgba(176, 141, 87, 0.10)",
            borderColor: "#b08d57",
            color: "#2c4767",
        },
    },
    emptyIcon: {
        fontSize: 36,
        color: "#b08d57",
        marginBottom: theme.spacing(0.5),
    },
    emptyText: {
        fontWeight: 600,
        fontSize: "0.9rem",
        color: "#2c4767",
    },
    emptyHint: {
        fontSize: "0.75rem",
        color: "#5a6675",
    },
    selectedFileRow: {
        margin: theme.spacing(0, 2.25, 1.5, 2.25),
        padding: theme.spacing(1, 1.25),
        background: "rgba(176, 141, 87, 0.10)",
        border: "1px solid rgba(176, 141, 87, 0.28)",
        borderRadius: 8,
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        fontSize: "0.85rem",
        color: "#2c4767",
        fontWeight: 500,
        overflow: "hidden",
    },
    selectedFileIcon: {
        color: "#b08d57",
        flexShrink: 0,
    },
    selectedFileName: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        flex: "1 1 auto",
    },
    progressWrap: {
        margin: theme.spacing(0, 2.25, 1.5, 2.25),
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1.5),
    },
    progressBar: {
        flex: 1,
    },
    progressLabel: {
        fontSize: "0.78rem",
        color: "#5a6675",
        fontWeight: 600,
        minWidth: 36,
        textAlign: "right",
    },
    actions: {
        marginTop: "auto",
        padding: theme.spacing(1.5, 2.25, 2, 2.25),
        borderTop: "1px solid rgba(176, 141, 87, 0.14)",
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        background: "#fbfaf7",
    },
    actionLabelWrap: {
        display: "inline-flex",
        flex: "1 1 auto",
        margin: 0,
    },
    actionPrimary: {
        flex: "1 1 auto",
        background: "linear-gradient(135deg, #2c4767 0%, #1e3553 100%)",
        color: "#ffffff",
        fontWeight: 600,
        textTransform: "none",
        fontSize: "0.88rem",
        padding: theme.spacing(0.9, 1.5),
        borderRadius: 8,
        boxShadow: "0 4px 12px rgba(44, 71, 103, 0.20)",
        "&:hover": {
            background: "linear-gradient(135deg, #1e3553 0%, #2c4767 100%)",
            boxShadow: "0 6px 16px rgba(44, 71, 103, 0.28)",
        },
    },
    actionSecondary: {
        flex: "1 1 auto",
        background: "#ffffff",
        color: "#2c4767",
        fontWeight: 600,
        textTransform: "none",
        fontSize: "0.88rem",
        padding: theme.spacing(0.9, 1.5),
        border: "1.5px solid rgba(44, 71, 103, 0.22)",
        borderRadius: 8,
        "&:hover": {
            background: "rgba(176, 141, 87, 0.10)",
            borderColor: "#b08d57",
        },
    },
    actionIconBtn: {
        background: "#ffffff",
        border: "1.5px solid rgba(44, 71, 103, 0.22)",
        borderRadius: 8,
        padding: 8,
        color: "#2c4767",
        transition: "background 180ms ease, color 180ms ease, border-color 180ms ease",
        "&:hover": {
            background: "#2c4767",
            color: "#ffffff",
            borderColor: "#2c4767",
        },
    },
    lightboxRoot: {
        '& .__react_modal_image__medium_img': {
            maxHeight: 'calc(100% - 160px)',
            maxWidth: 'calc(100% - 24px)',
            [theme.breakpoints.down('xs')]: {maxHeight: 'calc(100% - 240px)'},
        },
    },
    lightboxControls: {
        position: 'fixed',
        zIndex: 5100,
        bottom: theme.spacing(2),
        left: '50%',
        transform: 'translateX(-50%)',
        width: 'max-content',
        maxWidth: 'calc(100% - 24px)',
        boxSizing: 'border-box',
        padding: theme.spacing(1, 2),
        background: '#1a2733',
        color: '#ffffff',
        borderRadius: 8,
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.25)',
        '& button': {color: '#ffffff'},
    },
    lightboxActions: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(1),
        flexWrap: 'wrap',
    },
}));

function substrOfName(str) {
    if (!str) return "";
    if (str.length > 16) {
        return str.substr(0, 6) + "..." + str.substr(str.length - 8);
    }
    return str;
}

export default function PhotoUploadCard({
    photo,
    photoText,
    selectedFile,
    title,
    hint = "Nahrajte fotku/sken dokumentu. (max 20MB)",
    onFileChange,
    onUpload,
    onDownload,
    isLightboxOpen,
    onToggleLightbox,
    uploadProgress,
    canUpload = true,
    gridXs = 12,
    gridSm = 6,
    gridMd = 6,
    gridLg = 4,
}) {
    const classes = useStyles();
    const [cardRef, nearViewport] = useNearViewport();
    const [originalSource, setOriginalSource] = useState(null);
    useEffect(() => { if (!isLightboxOpen) setOriginalSource(null); }, [isLightboxOpen]);
    const originalRequested = Boolean(isLightboxOpen && originalSource === photo);
    const thumbnailSource = mediaPreviewUrl(photo, 'thumbnail');
    const privateImage = usePrivateImage(thumbnailSource, {
        enabled: Boolean(thumbnailSource && (nearViewport || isLightboxOpen)), loadBlob: fetchPreviewBlob
    });
    const screenImage = usePrivateImage(mediaPreviewUrl(photo, 'screen'), {
        enabled: Boolean(isLightboxOpen && !originalRequested), loadBlob: loadPriorityImage
    });
    const originalImage = usePrivateImage(privateMediaUrl(photo) ? photo : null, {
        enabled: Boolean(isLightboxOpen && originalRequested), loadBlob: loadPriorityImage
    });
    const enlarged = originalRequested ? originalImage : screenImage;
    const lightboxSource = enlarged.src || privateImage.src;
    const isUploaded = !!photo;
    const hasSelectedFile = !!(selectedFile && selectedFile.name);
    const isUploading = uploadProgress !== null && uploadProgress !== undefined;
    const inputId = `contained-button-${photoText}`;

    const handleFileChange = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        const error = validatePhotoFile(file);
        if (error) {
            NotificationManager.error(photoUploadErrorMessage(error), 'Chyba', 5000);
        } else {
            onFileChange(event);
        }
        event.target.value = '';
    };

    return (
        <Grid item xs={gridXs} sm={gridSm} md={gridMd} lg={gridLg} key={photoText}>
            <div className={classes.card} ref={cardRef}>
                <div className={classes.header}>
                    <div className={classes.titleWrap}>
                        <Typography component="h3" className={classes.title}>
                            {title}
                        </Typography>
                        <Typography component="span" className={classes.hint}>
                            {hint}
                        </Typography>
                    </div>
                    {isUploaded && (
                        <span className={classes.uploadedBadge}>
                            <CheckCircleOutlineIcon/>
                            Nahrané
                        </span>
                    )}
                </div>

                <div className={classes.body}>
                    {isUploaded ? (
                        !privateImage.src ? (
                            <div className={classes.empty}>
                                <Typography role={privateImage.error ? 'alert' : 'status'}>
                                    {privateImage.error || !thumbnailSource ? 'Náhľad sa nepodarilo načítať.'
                                        : nearViewport ? 'Načítavam obrázok…' : 'Náhľad sa načíta pri zobrazení.'}
                                </Typography>
                                {privateImage.error && <Button onClick={privateImage.retry}>Skúsiť znova</Button>}
                                {(privateImage.error || !thumbnailSource) && <Button onClick={onToggleLightbox}>Otvoriť fotografiu</Button>}
                            </div>
                        ) : (
                        <div
                            className={classes.preview}
                            onClick={onToggleLightbox}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault(); event.stopPropagation(); onToggleLightbox();
                                }
                            }}
                            role="button"
                            aria-label={`Zobraziť: ${title}`}
                            tabIndex={0}
                        >
                            <img src={privateImage.src || undefined} alt={privateImage.error ? `${title} – obrázok sa nepodarilo načítať` : title} className={classes.previewImg}/>
                            <div className={classes.previewOverlay}>
                                <ZoomInIcon/>
                                <span>Zobraziť</span>
                            </div>
                        </div>
                        )
                    ) : (
                        canUpload ? (
                            <label htmlFor={inputId} className={classes.empty}>
                                <CloudUploadOutlinedIcon className={classes.emptyIcon}/>
                                <span className={classes.emptyText}>Kliknite pre výber fotky</span>
                                <span className={classes.emptyHint}>JPG, PNG, GIF (max 20 MB)</span>
                            </label>
                        ) : (
                            <div className={classes.empty}>
                                <span className={classes.emptyText}>Dokument nie je nahraný</span>
                            </div>
                        )
                    )}
                    {isLightboxOpen && <Modal open onClose={onToggleLightbox} hideBackdrop style={{zIndex: 5000}}>
                        <div className={classes.lightboxRoot} role="dialog" aria-modal="true" aria-label={title} tabIndex={-1}>
                        <Lightbox medium={lightboxSource} large={lightboxSource} alt={title}
                            onClose={onToggleLightbox} showRotate={true} hideDownload={true}/>
                        <div className={classes.lightboxControls} role="region" aria-label={`Načítanie fotografie: ${title}`}
                            onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') event.stopPropagation(); }}>
                            {enlarged.loading && <>
                                <Typography role="status" variant="body2">
                                    {originalRequested ? 'Načítavam plné rozlíšenie…' : 'Načítavam väčšiu fotografiu…'}
                                </Typography>
                                <LinearProgress aria-label="Načítavanie fotografie"/>
                            </>}
                            {enlarged.error && <Typography role="alert" variant="body2">
                                {originalRequested ? 'Originál sa nepodarilo načítať.' : 'Väčší náhľad sa nepodarilo načítať.'}
                            </Typography>}
                            <div className={classes.lightboxActions}>
                                {enlarged.error && <Button onClick={enlarged.retry}>Skúsiť znova</Button>}
                                {!originalRequested && <Button onClick={() => setOriginalSource(photo)}
                                    disabled={!privateMediaUrl(photo)}>Plné rozlíšenie</Button>}
                                {originalRequested && originalImage.src && <Typography variant="body2">Plné rozlíšenie</Typography>}
                                {onDownload && <Button onClick={onDownload} startIcon={<GetAppIcon/>}>Stiahnuť originál</Button>}
                                <Button onClick={onToggleLightbox}>Zavrieť</Button>
                            </div>
                        </div>
                        </div>
                    </Modal>}
                </div>

                {hasSelectedFile && (
                    <div className={classes.selectedFileRow}>
                        <InsertDriveFileIcon fontSize="small" className={classes.selectedFileIcon}/>
                        <span className={classes.selectedFileName}>{substrOfName(selectedFile.name)}</span>
                    </div>
                )}

                {isUploading && (
                    <div className={classes.progressWrap}>
                        <Box className={classes.progressBar}>
                            <BorderLinearProgress
                                variant="determinate"
                                value={uploadProgress}
                            />
                        </Box>
                        <span className={classes.progressLabel}>{`${uploadProgress}%`}</span>
                    </div>
                )}

                {canUpload && <input
                    accept={PHOTO_ACCEPT}
                    style={{display: "none"}}
                    id={inputId}
                    type="file"
                    aria-label={`Vybrať fotku: ${title}`}
                    disabled={isUploading}
                    onChange={handleFileChange}
                />}

                <div className={classes.actions}>
                    {canUpload && (hasSelectedFile ? (
                        <Button
                            onClick={onUpload}
                            disabled={isUploading}
                            className={classes.actionPrimary}
                            startIcon={<CloudUploadIcon/>}
                        >
                            Nahrať
                        </Button>
                    ) : isUploaded ? (
                        <label htmlFor={inputId} className={classes.actionLabelWrap}>
                            <Button
                                component="span"
                                className={classes.actionSecondary}
                                startIcon={<PhotoCamera/>}
                                fullWidth
                            >
                                Zmeniť fotku
                            </Button>
                        </label>
                    ) : (
                        <label htmlFor={inputId} className={classes.actionLabelWrap}>
                            <Button
                                component="span"
                                className={classes.actionPrimary}
                                startIcon={<PhotoCamera/>}
                                fullWidth
                            >
                                Vybrať fotku
                            </Button>
                        </label>
                    ))}
                    {canUpload && hasSelectedFile && (
                        <Tooltip title="Vybrať inú fotku">
                            <IconButton
                                component="label"
                                htmlFor={inputId}
                                disabled={isUploading}
                                className={classes.actionIconBtn}
                                aria-label="Vybrať inú fotku"
                                size="small"
                            >
                                <PhotoCamera/>
                            </IconButton>
                        </Tooltip>
                    )}
                    {isUploaded && onDownload && (
                        <Tooltip title="Stiahnuť">
                            <IconButton
                                onClick={onDownload}
                                className={classes.actionIconBtn}
                                aria-label="Stiahnuť"
                                size="small"
                            >
                                <GetAppIcon fontSize="small"/>
                            </IconButton>
                        </Tooltip>
                    )}
                </div>
            </div>
        </Grid>
    );
}
